import { useEffect, useState } from "react";
import { Copy, Download, Instagram, Loader2, Share2, X } from "lucide-react";
import type { InstagramFormat, InstagramSharePost } from "@/lib/instagramShareRenderer";

type InstagramShareButtonProps = {
  post: InstagramSharePost;
  variant?: "icon" | "full";
  compact?: boolean;
  title?: string;
};

export default function InstagramShareButton({ post, variant = "icon", compact = false, title = "Für Instagram vorbereiten" }: InstagramShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [format, setFormat] = useState<InstagramFormat | null>(null);
  const [assetUrl, setAssetUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => () => {
    if (assetUrl) URL.revokeObjectURL(assetUrl);
  }, [assetUrl]);

  const closeDialog = () => {
    setOpen(false);
    setError("");
    setCopied(false);
  };

  const generateAsset = async (nextFormat: InstagramFormat) => {
    setGenerating(true);
    setError("");
    setCopied(false);
    try {
      const renderer = await import("@/lib/instagramShareRenderer");
      const blob = await renderer.renderInstagramAsset(post, nextFormat);
      setAssetUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return URL.createObjectURL(blob);
      });
      setCaption(nextFormat === "feed" ? renderer.buildInstagramCaption(post) : "");
      setFormat(nextFormat);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Grafik konnte nicht erstellt werden");
    } finally {
      setGenerating(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
    } catch {
      setError("Caption konnte nicht in die Zwischenablage kopiert werden");
    }
  };

  const fileName = format === "story"
    ? `immozwerg-story-${post.slug}.png`
    : `immozwerg-beitrag-${post.slug}.png`;

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        className={compact
          ? "w-11 h-11 inline-flex items-center justify-center rounded-xl text-[#E1306C] hover:text-white hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] transition-all bg-[#E1306C]/10 border border-[#E1306C]/30"
          : variant === "icon"
            ? "inline-flex items-center justify-center w-11 h-11 rounded-full bg-white/95 text-[#E1306C] shadow-sm border border-[#e6dcd2] hover:bg-[#f4eff8] hover:scale-105 transition-all"
            : "inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold text-white bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:scale-105 hover:shadow-md transition-all"}
        aria-label={title}
        title={title}
      >
        <Instagram size={compact ? 15 : 16} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-5 bg-[#1a1228]/65 backdrop-blur-sm"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDialog();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="instagram-share-title"
            className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#FAF8F5] shadow-2xl"
          >
            <header className="flex items-start justify-between gap-5 p-6 sm:p-8 border-b border-[#e6dcd2]">
              <div>
                <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#a066c2]">Instagram vorbereiten</p>
                <h2 id="instagram-share-title" className="mt-1 text-2xl font-bold text-[#2d1b4e]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Story oder Beitrag?
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">Erstelle die passende Grafik, lade sie herunter und veröffentliche sie in Instagram.</p>
              </div>
              <button
                type="button"
                onClick={closeDialog}
                className="w-11 h-11 inline-flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Schließen"
              >
                <X size={20} />
              </button>
            </header>

            <div className="p-6 sm:p-8 space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => generateAsset("story")}
                  disabled={generating}
                  className="flex flex-col items-center justify-center p-5 rounded-2xl border-2 border-[#e6dcd2] bg-white hover:border-[#a066c2] hover:shadow-md transition-all group disabled:opacity-50"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#f4eff8] text-[#631172] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Instagram size={24} />
                  </div>
                  <span className="font-bold text-[#2d1b4e]">Instagram Story</span>
                  <span className="text-xs text-gray-500 mt-1">9:16 Format (1080×1920)</span>
                </button>

                <button
                  type="button"
                  onClick={() => generateAsset("feed")}
                  disabled={generating}
                  className="flex flex-col items-center justify-center p-5 rounded-2xl border-2 border-[#e6dcd2] bg-white hover:border-[#a066c2] hover:shadow-md transition-all group disabled:opacity-50"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#f4eff8] text-[#631172] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    <Share2 size={24} />
                  </div>
                  <span className="font-bold text-[#2d1b4e]">Instagram Beitrag</span>
                  <span className="text-xs text-gray-500 mt-1">4:5 Format (1080×1350) + Caption</span>
                </button>
              </div>

              {generating && (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-500">
                  <Loader2 size={32} className="animate-spin text-[#631172]" />
                  <p className="text-sm">Grafik wird für Instagram vorbereitet…</p>
                </div>
              )}

              {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
                  {error}
                </div>
              )}

              {assetUrl && !generating && (
                <div className="space-y-6 pt-4 border-t border-[#e6dcd2]">
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <div className="w-40 sm:w-48 aspect-[9/16] bg-gray-900 rounded-2xl overflow-hidden shadow-md flex items-center justify-center flex-shrink-0">
                      <img src={assetUrl} alt="Vorschau" className="w-full h-full object-contain" />
                    </div>
                    <div className="space-y-4 flex-1 w-full">
                      <div>
                        <h3 className="font-bold text-[#2d1b4e]">Grafik bereit</h3>
                        <p className="text-xs text-gray-600 mt-1">Lade das Bild herunter und füge es in Instagram ein.</p>
                      </div>
                      <a
                        href={assetUrl}
                        download={fileName}
                        className="inline-flex items-center justify-center gap-2 w-full py-3 px-5 rounded-xl font-bold text-white shadow-md hover:scale-[1.02] transition-all"
                        style={{ background: "linear-gradient(135deg, #631172, #a066c2)" }}
                      >
                        <Download size={18} /> Grafik herunterladen
                      </a>
                    </div>
                  </div>

                  {caption && (
                    <div className="space-y-3 pt-4 border-t border-[#e6dcd2]">
                      <div className="flex items-center justify-between">
                        <h4 className="font-bold text-[#2d1b4e] text-sm">Empfohlene Caption (Beitrag)</h4>
                        <button
                          type="button"
                          onClick={copyCaption}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[#e6dcd2] text-[#2d1b4e] hover:border-[#a066c2] transition-colors"
                        >
                          {copied ? <span className="text-emerald-600 font-bold">Kopiert!</span> : <><Copy size={13} /> Text kopieren</>}
                        </button>
                      </div>
                      <div className="p-4 rounded-xl bg-white border border-[#e6dcd2] text-xs leading-relaxed text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
                        {caption}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <footer className="px-6 sm:px-8 py-4 bg-gray-50 border-t border-[#e6dcd2] text-right">
              <button
                type="button"
                onClick={closeDialog}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-200 transition-colors"
              >
                Fertig
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}
